import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth-context';
import { useWorkspaceAccess } from './lib/access';
import { AppShell } from './components/layout/AppShell';
import { Login } from './pages/Login';
import { SsoCallback } from './pages/SsoCallback';
import { AccessDenied } from './pages/AccessDenied';
import { Terms } from './pages/Terms';
import { Dashboard } from './pages/Dashboard';
import { QuickScan } from './pages/QuickScan';
import { Targets } from './pages/Targets';
import { AssetGroups } from './pages/AssetGroups';
import { Engagements } from './pages/Engagements';
import { EngagementDetail } from './pages/EngagementDetail';
import { ScopePolicies } from './pages/ScopePolicies';
import { Scans } from './pages/Scans';
import { RunDetail } from './pages/RunDetail';
import { Findings } from './pages/Findings';
import { FindingDetail } from './pages/FindingDetail';
import { AttackPaths } from './pages/AttackPaths';
import { Reports } from './pages/Reports';
import { Retests } from './pages/Retests';
import { AuditLog } from './pages/AuditLog';
import { ApiKeys } from './pages/ApiKeys';
import { KillSwitch } from './pages/KillSwitch';
import { Users } from './pages/Users';
import { Account } from './pages/Account';
import { GitHubEnterprise } from './pages/GitHubEnterprise';
import { GitHubCallback } from './pages/GitHubCallback';
import { Devices } from './pages/Devices';
import { Brokers } from './pages/Brokers';
import { AdminConsole } from './pages/admin/AdminConsole';
import { WorkspaceDetail } from './pages/admin/WorkspaceDetail';
import { NewWorkspace } from './pages/admin/NewWorkspace';
import { PlatformKeys } from './pages/admin/PlatformKeys';
import { SkeletonRows } from './components/ui/skeleton';

export function Protected({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const access = useWorkspaceAccess();

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!access.checked) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <SkeletonRows rows={3} />
        </div>
      </div>
    );
  }

  if (access.isError) {
    return <AccessDenied reason="not_a_member" />;
  }

  return <AppShell>{children}</AppShell>;
}

/**
 * Guards /login (and any other public-only route) the other direction from
 * `Protected`: once a login succeeds — password, API key, or SSO — this
 * redirects away instead of leaving the user stranded on the login screen.
 * Any of `useAuth()`'s login methods setting `principal` is enough to
 * trigger it; no individual login handler needs its own navigate() call.
 */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <Login />
          </PublicOnly>
        }
      />
      <Route path="/auth/sso/callback" element={<SsoCallback />} />
      <Route path="/terms" element={<Terms />} />
      <Route
        path="/"
        element={
          <Protected>
            <Dashboard />
          </Protected>
        }
      />
      <Route
        path="/quick-scan"
        element={
          <Protected>
            <QuickScan />
          </Protected>
        }
      />
      <Route
        path="/targets"
        element={
          <Protected>
            <Targets />
          </Protected>
        }
      />
      <Route
        path="/asset-groups"
        element={
          <Protected>
            <AssetGroups />
          </Protected>
        }
      />
      <Route
        path="/engagements"
        element={
          <Protected>
            <Engagements />
          </Protected>
        }
      />
      <Route
        path="/engagements/:id"
        element={
          <Protected>
            <EngagementDetail />
          </Protected>
        }
      />
      <Route
        path="/scope-policies"
        element={
          <Protected>
            <ScopePolicies />
          </Protected>
        }
      />
      <Route path="/runs" element={<Navigate to="/scans" replace />} />
      <Route
        path="/scans"
        element={
          <Protected>
            <Scans />
          </Protected>
        }
      />
      <Route
        path="/runs/:id"
        element={
          <Protected>
            <RunDetail />
          </Protected>
        }
      />
      <Route
        path="/findings"
        element={
          <Protected>
            <Findings />
          </Protected>
        }
      />
      <Route
        path="/findings/:id"
        element={
          <Protected>
            <FindingDetail />
          </Protected>
        }
      />
      <Route
        path="/attack-paths"
        element={
          <Protected>
            <AttackPaths />
          </Protected>
        }
      />
      <Route
        path="/reports"
        element={
          <Protected>
            <Reports />
          </Protected>
        }
      />
      <Route
        path="/retests"
        element={
          <Protected>
            <Retests />
          </Protected>
        }
      />
      <Route
        path="/brokers"
        element={
          <Protected>
            <Brokers />
          </Protected>
        }
      />
      <Route
        path="/audit-log"
        element={
          <Protected>
            <AuditLog />
          </Protected>
        }
      />
      <Route
        path="/api-keys"
        element={
          <Protected>
            <ApiKeys />
          </Protected>
        }
      />
      <Route
        path="/kill-switch"
        element={
          <Protected>
            <KillSwitch />
          </Protected>
        }
      />
      <Route
        path="/users"
        element={
          <Protected>
            <Users />
          </Protected>
        }
      />
      <Route
        path="/account"
        element={
          <Protected>
            <Account />
          </Protected>
        }
      />
      <Route
        path="/github"
        element={
          <Protected>
            <GitHubEnterprise />
          </Protected>
        }
      />
      <Route
        path="/integrations/github/callback"
        element={
          <Protected>
            <GitHubCallback />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected>
            <AdminConsole />
          </Protected>
        }
      />
      <Route
        path="/admin/workspaces/new"
        element={
          <Protected>
            <NewWorkspace />
          </Protected>
        }
      />
      <Route
        path="/admin/workspaces/:id"
        element={
          <Protected>
            <WorkspaceDetail />
          </Protected>
        }
      />
      <Route
        path="/admin/platform-keys"
        element={
          <Protected>
            <PlatformKeys />
          </Protected>
        }
      />
      <Route path="/internal-testing" element={<Navigate to="/devices" replace />} />
      <Route path="/endpoint-testing" element={<Navigate to="/devices?tab=endpoints" replace />} />
      <Route
        path="/devices"
        element={
          <Protected>
            <Devices />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
